/**
 * Accounts whose roles were all withdrawn (owner request 2026-11-03): they drop out of the lists above, so
 * they are found here to give a role again (the same account) or to delete for good.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Trash2 } from "lucide-react";
import type { FormerMember, FormerMembersResponse, Role } from "@mubin/contracts";
import { displayPhone } from "@mubin/contracts";
import { Empty, ErrorNote, Loading, Modal, useSubmit } from "./ui";
import { CreateUserModal } from "./user-forms";
import { api, errorText, send } from "../lib/api";
import { roleLabel } from "../lib/labels";
import { useCan, useOrg } from "../lib/org";

export function FormerMembers() {
  const org = useOrg();
  const can = useCan();
  const [restoring, setRestoring] = useState<FormerMember | null>(null);
  const [deleting, setDeleting] = useState<FormerMember | null>(null);
  const list = useQuery({
    queryKey: ["users", "former", org?.id],
    queryFn: () => api<FormerMembersResponse>(`/v1/users/former?organizationId=${org!.id}`),
    enabled: !!org,
  });

  return (
    <>
      <p className="muted small" style={{ padding: "0 4px 10px" }}>
        من سُحبت منه كل أدواره يبقى حسابه، ولا يظهر في التبويبات الأخرى. أعد له دورًا فيرجع بحسابه نفسه، أو
        احذف حسابه نهائيًا.
      </p>
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <Empty title="تعذر التحميل">{errorText(list.error)}</Empty>
      ) : list.data.members.length === 0 ? (
        <Empty title="لا يوجد أحد سُحبت أدواره" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>الاسم</th>
                <th>الجوال</th>
                <th>آخر دور</th>
                <th>سُحب في</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data.members.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>
                    <bdi dir="ltr">{m.phone ? displayPhone(m.phone) : "—"}</bdi>
                  </td>
                  <td>{roleLabel[m.lastRole as Role] ?? m.lastRole}</td>
                  <td className="muted small">
                    {new Date(m.withdrawnAt).toLocaleDateString("ar-SA-u-ca-islamic-umalqura", {
                      day: "numeric",
                      month: "long",
                    })}
                  </td>
                  <td>
                    <div className="row-actions">
                      {can.has("users.manage") && m.phone && (
                        <button className="btn btn-secondary btn-sm" onClick={() => setRestoring(m)}>
                          <RotateCcw size={15} strokeWidth={1.75} /> إعادة دور
                        </button>
                      )}
                      {can.has("deletion.manage") && (
                        <button className="btn btn-danger btn-sm" onClick={() => setDeleting(m)}>
                          <Trash2 size={15} strokeWidth={1.75} /> حذف نهائي
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CreateUserModal
        open={restoring !== null}
        onClose={() => setRestoring(null)}
        prefill={
          restoring
            ? { name: restoring.name, phone: restoring.phone ?? "", gender: restoring.gender ?? "male" }
            : undefined
        }
      />
      {deleting && <DeleteAccountModal person={deleting} onClose={() => setDeleting(null)} />}
    </>
  );
}

/** Deletes an account for good, after the administrator confirms. */
export function DeleteAccountModal({
  person,
  onClose,
  onDeleted,
}: {
  person: { id: string; name: string };
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const org = useOrg();
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  return (
    <Modal title={`حذف حساب ${person.name} نهائيًا`} open onClose={onClose}>
      <div className="form">
        <ErrorNote error={error} />
        <p>
          تُمسح بياناته الشخصية (الاسم والجوال والجنس) ولا يستطيع الدخول أبدًا، وتُسحب أدواره وتسجيلاته. سجلات
          التسميع والحضور تبقى بلا اسمه حتى لا تختل التقارير. <b>لا يمكن التراجع عن هذا.</b>
        </p>
        <div className="form-actions">
          <button
            className="btn btn-danger"
            disabled={busy || !org}
            onClick={() =>
              void run(async () => {
                await send("POST", `/v1/users/${person.id}/delete`, { organizationId: org!.id });
                await qc.invalidateQueries({ queryKey: ["users"] });
                onClose();
                onDeleted?.();
              }, errorText)
            }
          >
            {busy ? "جارٍ الحذف…" : "حذف نهائي"}
          </button>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            إلغاء
          </button>
        </div>
      </div>
    </Modal>
  );
}
